"use client";

import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { SliderInput } from "@/components/blog/SliderInput";
import RoiCalculatorIcon from "@/components/icons/RoiCalculatorIcon";
import { useAnimatedValue } from "@/hooks/useAnimatedValue";
import {
  formatCurrency,
  formatNumber,
} from "@/utils/roiCalculator";

const CONVERSION_RATE = 0.3;
const GAIL_MONTHLY_COST = process.env.NEXT_PUBLIC_GAIL_MONTHLY_COST
  ? Number(process.env.NEXT_PUBLIC_GAIL_MONTHLY_COST)
  : 480;
const GAIL_ANNUAL_COST = GAIL_MONTHLY_COST * 12;

const DEFAULTS = {
  monthlyVolume: 500,
  missedPct: 25,
  avgPremium: 1500,
} as const;

const RANGES = {
  monthlyVolume: { min: 50, max: 5000, step: 50 },
  missedPct: { min: 5, max: 60, step: 1 },
  avgPremium: { min: 200, max: 10000, step: 100 },
} as const;

interface ROICalculatorProps {
  children?: ReactNode;
}

export function ROICalculator({ children }: Readonly<ROICalculatorProps>) {
  const { t } = useTranslation();

  const [monthlyVolume, setMonthlyVolume] = useState<number>(
    DEFAULTS.monthlyVolume,
  );
  const [missedPct, setMissedPct] = useState<number>(DEFAULTS.missedPct);
  const [avgPremium, setAvgPremium] = useState<number>(DEFAULTS.avgPremium);

  const missedCalls = monthlyVolume * (missedPct / 100);
  const recoveredCalls = missedCalls * CONVERSION_RATE;
  const annualRevenue = recoveredCalls * avgPremium * 12;
  const roiMultiple = annualRevenue / GAIL_ANNUAL_COST;

  const animatedRevenue = useAnimatedValue(annualRevenue);
  const animatedCalls = useAnimatedValue(Math.round(recoveredCalls * 12));
  const animatedRoi = useAnimatedValue(Math.max(1, Math.round(roiMultiple)));

  return (
    <div className="bg-white shadow-lg my-10 border border-gray-200 rounded-2xl overflow-hidden not-prose">
      <div className="border-gail-primary border-t-4" />

      <div className="px-5 md:px-8 pt-6 md:pt-8 pb-2">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="flex justify-center items-center bg-gail-primary/10 rounded-lg w-9 h-9">
            <RoiCalculatorIcon
              className="w-5 h-5 text-gail-primary"
              aria-hidden="true"
            />
          </div>
          <h3 className="font-sans font-bold text-gray-900 text-lg">
            {t("roiCalculator.title")}
          </h3>
        </div>
        {children && (
          <div className="mt-1 mb-0 max-w-xl text-gail-gray text-sm leading-relaxed">
            {children}
          </div>
        )}
      </div>

      <div className="space-y-5 px-5 md:px-8 py-5">
        <SliderInput
          id="roi-monthly-volume"
          label={t("roiCalculator.monthlyVolume")}
          value={monthlyVolume}
          onChange={setMonthlyVolume}
          min={RANGES.monthlyVolume.min}
          max={RANGES.monthlyVolume.max}
          step={RANGES.monthlyVolume.step}
          formatMin={formatNumber(RANGES.monthlyVolume.min)}
          formatMax={formatNumber(RANGES.monthlyVolume.max)}
          formatValue={formatNumber}
        />

        <SliderInput
          id="roi-missed-pct"
          label={t("roiCalculator.missedPct")}
          value={missedPct}
          onChange={setMissedPct}
          min={RANGES.missedPct.min}
          max={RANGES.missedPct.max}
          step={RANGES.missedPct.step}
          formatMin={`${RANGES.missedPct.min}%`}
          formatMax={`${RANGES.missedPct.max}%`}
          formatValue={(v) => `${v}%`}
        />

        <SliderInput
          id="roi-avg-premium"
          label={t("roiCalculator.avgPremium")}
          value={avgPremium}
          onChange={setAvgPremium}
          min={RANGES.avgPremium.min}
          max={RANGES.avgPremium.max}
          step={RANGES.avgPremium.step}
          formatMin={formatCurrency(RANGES.avgPremium.min)}
          formatMax={formatCurrency(RANGES.avgPremium.max)}
          formatValue={formatCurrency}
        />
      </div>

      <div className="bg-gail-primary-light mx-5 md:mx-8 mb-5 md:mb-8 p-5 md:p-6 rounded-xl">
        <p className="mb-1 font-sans font-medium text-gail-gray text-sm text-center">
          {t("roiCalculator.projectedRevenue")}
        </p>
        <p
          className="font-sans font-bold text-gail-primary text-4xl md:text-5xl text-center tracking-tight"
          aria-live="polite"
        >
          {formatCurrency(animatedRevenue)}
        </p>

        <div className="gap-3 md:gap-4 grid grid-cols-3 mt-5">
          <div className="bg-white/70 p-3 rounded-lg text-center">
            <p className="font-sans font-bold text-gray-900 text-lg md:text-xl">
              {formatNumber(animatedCalls)}
            </p>
            <p className="font-sans text-gail-gray text-xs">
              {t("roiCalculator.callsRecovered")}
            </p>
          </div>
          <div className="bg-white/70 p-3 rounded-lg text-center">
            <p className="font-sans font-bold text-gray-900 text-lg md:text-xl">
              {formatCurrency(GAIL_ANNUAL_COST)}
            </p>
            <p className="font-sans text-gail-gray text-xs">
              {t("roiCalculator.gailAnnualCost")}
            </p>
          </div>
          <div className="bg-white/70 p-3 rounded-lg text-center">
            <p className="font-sans font-bold text-gail-primary text-lg md:text-xl">
              {roiMultiple >= 1 ? `${animatedRoi}x` : "<1x"}
            </p>
            <p className="font-sans text-gail-gray text-xs">
              {t("roiCalculator.roi")}
            </p>
          </div>
        </div>

        <p className="mt-4 font-sans text-gail-gray text-xs text-center">
          {t("roiCalculator.footnote")}
        </p>
      </div>
    </div>
  );
}
